import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Crown, Check, Loader2, Sparkles, Star } from "lucide-react";

const FREE_FEATURES = [
  "5 AI coach sessions per day",
  "Daily writing prompts",
  "Grammar drills",
  "Vocabulary practice",
  "3 revision dimensions per analysis",
  "Bronze · Silver · Gold tier badges",
  "Streak tracker + 7 badges",
];

const PREMIUM_FEATURES = [
  "Unlimited AI coach sessions",
  "All 6 revision dimensions per analysis (rhetorical impact, MLA, audience awareness…)",
  "Advanced SAT/AP rubric coaching",
  "Diamond tier (50+ sessions) on every Growth Path",
  "Adaptive personalized coaching path",
  "Deeper analytical & rhetorical feedback",
  "Priority Mr. Ramsey coaching tone",
  "Everything in Free, no limits",
];

export default function Upgrade() {
  const { user } = useAuth();
  const [usage, setUsage] = useState(null);
  const [founderStatus, setFounderStatus] = useState(null);
  const [loading, setLoading] = useState(null); // "founder" | "student" | null

  useEffect(() => {
    api.get("/billing/usage").then(r => setUsage(r.data)).catch(() => {});
    api.get("/founder/status").then(r => setFounderStatus(r.data)).catch(() => {});
  }, []);

  const subscribe = async (plan) => {
    setLoading(plan);
    try {
      const { data } = await api.post("/billing/subscribe", { plan, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.code === "founder_sold_out") {
        toast.error(detail.message);
      } else {
        toast.error("Couldn't start checkout. Try again.");
      }
      setLoading(null);
    }
  };

  if (user?.is_premium) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="tactile-card p-10 text-center bg-gradient-to-br from-amber-50 to-rose-50 border-amber-100">
          <div className="w-16 h-16 rounded-full bg-amber-400 text-slate-900 grid place-items-center mx-auto mb-4">
            <Crown size={28}/>
          </div>
          <h1 className="text-3xl font-bold mb-2" style={{fontFamily:'Outfit'}}>You're Premium 🎉</h1>
          <p className="text-slate-700 mb-1">Unlimited coaching unlocked.</p>
          {user.premium_expires_at && (
            <p data-testid="premium-expires" className="text-sm text-slate-500">
              Renews on {new Date(user.premium_expires_at).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-2xl bg-amber-100 text-amber-700 grid place-items-center"><Crown size={20}/></div>
          <h1 className="text-3xl font-bold" style={{fontFamily:'Outfit'}}>Upgrade to Premium</h1>
        </div>
        <p className="text-slate-600 max-w-2xl">Unlimited coaching. Deeper feedback. Full Growth Paths. $9.99/month, cancel anytime.</p>
      </div>

      {usage && !usage.is_premium && (
        <div data-testid="usage-bar" className="tactile-card p-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase font-bold tracking-widest text-slate-500">Today's usage</div>
            <div className="text-xl font-bold" style={{fontFamily:'Outfit'}}>
              {usage.used} / {usage.limit} free AI sessions
            </div>
          </div>
          <div className="w-full sm:w-64 h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-rose-500 transition-all" style={{width: `${(usage.used/usage.limit)*100}%`}}/>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-6">
        <div className="tactile-card p-7">
          <div className="text-xs uppercase font-bold tracking-widest text-slate-500 mb-1">Free</div>
          <div className="text-4xl font-bold mb-1" style={{fontFamily:'Outfit'}}>$0</div>
          <div className="text-sm text-slate-500 mb-5">Forever</div>
          <ul className="space-y-2.5">
            {FREE_FEATURES.map((f,i)=>(
              <li key={i} className="flex items-start gap-2 text-slate-700 dark:text-slate-300">
                <Check size={16} className="text-slate-400 mt-0.5 flex-shrink-0"/>
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* FOUNDER PLAN — highlighted */}
        <div data-testid="upgrade-founder-card"
          className="p-7 rounded-3xl border-2 border-amber-600 shadow-xl bg-gradient-to-br from-amber-500 to-orange-500 text-white relative overflow-hidden">
          <div className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-slate-900 text-amber-300 px-3 py-1 text-xs font-bold uppercase tracking-wider">
            <Star size={12}/> Limited
          </div>
          <div className="text-xs uppercase font-bold tracking-widest text-amber-100 mb-1">Founder Plan</div>
          <div className="text-4xl font-bold mb-1" style={{fontFamily:'Outfit'}}>$6.99<span className="text-xl text-amber-100">/mo</span></div>
          <div className="text-sm text-amber-100 mb-5">
            {founderStatus
              ? `${founderStatus.seats_remaining} of ${founderStatus.seat_limit} seats left · Locked for 12 months`
              : "Locked for 12 months"}
          </div>
          <ul className="space-y-2.5 mb-6">
            {[
              "Everything in Premium",
              "Founder Badge on your profile",
              "Locked $6.99/mo for 12 months",
              "Early access to every new feature",
              "Direct line to the WriterSpark team",
            ].map((f, i) => (
              <li key={i} className="flex items-start gap-2">
                <Check size={16} className="text-slate-900 mt-0.5 flex-shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          {founderStatus?.sold_out ? (
            <button disabled data-testid="upgrade-founder-soldout"
              className="w-full bg-slate-200 text-slate-700 font-bold rounded-full py-3.5 opacity-80">
              Sold out — join waitlist
            </button>
          ) : (
            <button data-testid="upgrade-founder-checkout" onClick={() => subscribe("founder")} disabled={loading === "founder"}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-full py-3.5 inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {loading === "founder" ? <><Loader2 className="animate-spin" size={16}/> Redirecting…</> : <><Crown size={16}/> Claim Founder seat</>}
            </button>
          )}
          <p className="text-xs text-amber-100 text-center mt-3">Secure checkout by Stripe</p>
        </div>

        <div data-testid="upgrade-student-card"
          className="p-7 rounded-3xl border-2 border-indigo-700 shadow-xl bg-gradient-to-br from-indigo-600 to-violet-600 text-white relative overflow-hidden">
          <div className="text-xs uppercase font-bold tracking-widest text-indigo-200 mb-1">Premium</div>
          <div className="text-4xl font-bold mb-1" style={{fontFamily:'Outfit'}}>$9.99<span className="text-xl text-indigo-200">/mo</span></div>
          <div className="text-sm text-indigo-200 mb-5">Cancel anytime</div>
          <ul className="space-y-2.5 mb-6">
            {PREMIUM_FEATURES.map((f,i)=>(
              <li key={i} className="flex items-start gap-2">
                <Check size={16} className="text-amber-300 mt-0.5 flex-shrink-0"/>
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <button data-testid="upgrade-checkout" onClick={() => subscribe("student")} disabled={loading === "student"}
            className="w-full bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold rounded-full py-3.5 tactile-shadow-amber inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {loading === "student" ? <><Loader2 className="animate-spin" size={16}/> Redirecting…</> : <><Sparkles size={16}/> Upgrade for $9.99/mo</>}
          </button>
          <p className="text-xs text-indigo-200 text-center mt-3">Secure checkout by Stripe</p>
        </div>
      </div>

      <p className="text-center text-sm text-slate-500">
        Questions? <Link to="/contact" className="text-indigo-700 font-semibold">Contact us</Link>
      </p>
    </div>
  );
}
